import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { BookOpen, Brain, ChevronRight, Highlighter, StickyNote, Pencil, Trash2, Palette } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Get auth token
const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const Revision = () => {
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubject, setSelectedSubject] = useState('');
  const [revisionContent, setRevisionContent] = useState([]);
  const [currentSubjectData, setCurrentSubjectData] = useState(null);
  const [currentTopic, setCurrentTopic] = useState(null);
  const [showQuiz, setShowQuiz] = useState(false);
  const [userNotes, setUserNotes] = useState([]);
  const [showAddNote, setShowAddNote] = useState(false);
  const [newNote, setNewNote] = useState({ title: '', content: '' });
  const [editingNote, setEditingNote] = useState(null);
  const [showEditNote, setShowEditNote] = useState(false);
  const [highlightedText, setHighlightedText] = useState([]);
  const [highlights, setHighlights] = useState([]);
  const [showHighlightMenu, setShowHighlightMenu] = useState(false);
  const [highlightMenuPosition, setHighlightMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedText, setSelectedText] = useState('');
  const [showHighlightNote, setShowHighlightNote] = useState(false);
  const [currentHighlight, setCurrentHighlight] = useState(null);
  const [highlightNote, setHighlightNote] = useState('');
  const [quizQuestions, setQuizQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [score, setScore] = useState(0);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentTopicIndex, setCurrentTopicIndex] = useState(0);
  const [shouldScrollToTopic, setShouldScrollToTopic] = useState(false);
  const topicContentRef = useRef(null);

  const categories = {
    "Mathematics": ["Algebra", "Calculus", "Geometry", "Statistics", "Trigonometry", "Linear Algebra", "Differential Equations"],
    "Science": ["Physics", "Chemistry", "Biology", "Earth Science", "Organic Chemistry", "Biochemistry", "Human Anatomy", "Microbiology", "Genetics", "Environmental Science", "Public Health"],
    "English": ["Literature", "Writing", "Grammar", "Poetry"],
    "History": ["World History", "American History", "European History", "Ancient Civilizations"],
    "Languages": ["Spanish", "French", "German", "Mandarin"],
    "Computer Science": ["Programming Fundamentals", "Data Structures", "Web Development", "Discrete Mathematics", "Database Systems", "Operating Systems", "Computer Networks", "Artificial Intelligence", "Software Engineering"],
    "Arts": ["Music Theory", "Art History", "Visual Arts", "Performing Arts"],
    "Social Studies": ["Economics", "Psychology", "Sociology", "Political Science"],
    "Physics": ["Thermodynamics", "Quantum Mechanics"],
    "Business": ["Microeconomics", "Macroeconomics", "Marketing", "Financial Accounting", "Organizational Behavior", "Business Law", "Supply Chain Management", "Business Analytics", "Project Management"]
  };

  useEffect(() => {
    fetchRevisionContent();
  }, []);

  // Scroll to top of topic content when currentTopic changes (only for Next/Previous navigation)
  useEffect(() => {
    if (currentTopic && topicContentRef.current && shouldScrollToTopic) {
      // Reset the flag immediately to prevent multiple scrolls
      setShouldScrollToTopic(false);
      
      // Use setTimeout to ensure DOM is fully updated before scrolling
      // This is especially important on mobile where layout changes take longer
      setTimeout(() => {
        const element = topicContentRef.current;
        if (element) {
          const rect = element.getBoundingClientRect();
          const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
          const targetPosition = rect.top + scrollTop - 20; // 20px offset from top
          
          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, 100); // Small delay to ensure DOM is updated
    }
  }, [currentTopic, shouldScrollToTopic]);

  const fetchRevisionContent = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/revision/content`);
      setRevisionContent(response.data);
    } catch (error) {
      console.error('Error fetching revision content:', error);
      toast.error('Failed to load revision content');
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectSelect = async (category, subject) => {
    setSelectedCategory(category);
    setSelectedSubject(subject);
    
    try {
      // URL encode the subject name to handle spaces and special characters
      const encodedSubject = encodeURIComponent(subject);
      const response = await axios.get(`${API}/revision/content/${encodedSubject}`);
      setCurrentSubjectData(response.data);
      
      if (response.data.topics && response.data.topics.length > 0) {
        setCurrentTopic(response.data.topics[0]);
        setCurrentTopicIndex(0);
      }
      
      // Fetch personal notes for this subject
      await fetchPersonalNotes(subject);
      
      // Fetch highlights for this subject
      await fetchHighlights(subject);
    } catch (error) {
      console.error('Error fetching subject content:', error);
      toast.error('Content not available yet');
      setCurrentSubjectData(null);
      setCurrentTopic(null);
    }
  };

  const fetchPersonalNotes = async (subject) => {
    try {
      // URL encode the subject name
      const encodedSubject = encodeURIComponent(subject);
      const response = await axios.get(`${API}/personal-notes/${encodedSubject}`, {
        headers: getAuthHeader()
      });
      setUserNotes(response.data);
    } catch (error) {
      console.error('Error fetching personal notes:', error);
      // Don't show error toast for notes, just silently fail
    }
  };

  const fetchHighlights = async (subject) => {
    try {
      const encodedSubject = encodeURIComponent(subject);
      const response = await axios.get(`${API}/highlights/${encodedSubject}`, {
        headers: getAuthHeader()
      });
      setHighlights(response.data);
    } catch (error) {
      console.error('Error fetching highlights:', error);
    }
  };

  const handleTextSelect = () => {
    const selection = window.getSelection();
    const text = selection.toString().trim();
    
    if (text && text.length > 0) {
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      setSelectedText(text);
      setHighlightMenuPosition({
        x: rect.left + rect.width / 2,
        y: rect.top + window.scrollY - 60
      });
      setShowHighlightMenu(true);
    } else {
      setShowHighlightMenu(false);
    }
  };

  const createHighlight = async (color) => {
    if (!selectedText || !currentTopic) return;
    
    try {
      // Create highlight in database
      const highlightData = {
        subject_name: selectedSubject,
        topic_title: currentTopic.title,
        text: selectedText,
        color: color,
        note: ''
      };
      
      const response = await axios.post(`${API}/highlights`, highlightData, {
        headers: getAuthHeader()
      });
      
      const newHighlight = { ...highlightData, id: response.data.id };
      setHighlights([...highlights, newHighlight]);
      
      // Show the note dialog to add personal notes
      setCurrentHighlight(newHighlight);
      setHighlightNote('');
      setShowHighlightNote(true);
      
      setShowHighlightMenu(false);
      setSelectedText('');
      window.getSelection().removeAllRanges();
      
      toast.success(`Text highlighted in ${color}!`);
    } catch (error) {
      console.error('Error creating highlight:', error);
      toast.error('Failed to create highlight');
    }
  };

  const saveHighlightNote = async () => {
    if (!currentHighlight) return;
    
    try {
      // Update highlight with note
      await axios.put(`${API}/highlights/${currentHighlight.id}`, 
        { note: highlightNote },
        { headers: getAuthHeader() }
      );
      
      // Update local state
      setHighlights(highlights.map(h => 
        h.id === currentHighlight.id ? { ...h, note: highlightNote } : h
      ));
      
      // Also create a personal note with highlighted text + user's note
      const colorEmojis = {
        yellow: '🟡',
        green: '🟢',
        blue: '🔵',
        pink: '🌸',
        purple: '🟣'
      };
      
      const noteContent = `**Highlighted Text:**\n${currentHighlight.text}\n\n**My Notes:**\n${highlightNote || '(Add your notes here)'}`;
      
      const noteData = {
        subject_name: currentHighlight.subject_name,
        title: `${colorEmojis[currentHighlight.color]} ${currentHighlight.topic_title.substring(0, 30)}...`,
        content: noteContent
      };
      
      await axios.post(`${API}/personal-notes`, noteData, {
        headers: getAuthHeader()
      });
      
      // Refresh notes
      await fetchPersonalNotes(currentHighlight.subject_name);
      
      setShowHighlightNote(false);
      setCurrentHighlight(null);
      setHighlightNote('');
      toast.success('Highlight and note saved!');
    } catch (error) {
      console.error('Error saving highlight note:', error);
      toast.error('Failed to save note');
    }
  };

  const deleteHighlight = async (highlightId) => {
    if (window.confirm('Delete this highlight?')) {
      try {
        await axios.delete(`${API}/highlights/${highlightId}`, {
          headers: getAuthHeader()
        });
        
        setHighlights(highlights.filter(h => h.id !== highlightId));
        toast.success('Highlight deleted!');
      } catch (error) {
        console.error('Error deleting highlight:', error);
        toast.error('Failed to delete highlight');
      }
    }
  };

  const renderHighlightedContent = () => {
    if (!currentTopic) return null;
    
    const topicHighlights = highlights.filter(h => h.topic_title === currentTopic.title);
    
    if (topicHighlights.length === 0) {
      return <pre className="whitespace-pre-wrap font-sans text-base leading-relaxed">{currentTopic.content}</pre>;
    }
    
    let content = currentTopic.content;
    const colorClasses = {
      yellow: 'bg-yellow-200 dark:bg-yellow-500/30',
      green: 'bg-green-200 dark:bg-green-500/30',
      blue: 'bg-blue-200 dark:bg-blue-500/30',
      pink: 'bg-pink-200 dark:bg-pink-500/30',
      purple: 'bg-purple-200 dark:bg-purple-500/30'
    };
    
    const sortedHighlights = [...topicHighlights].sort((a, b) => b.text.length - a.text.length);
    
    sortedHighlights.forEach(highlight => {
      const escapedText = highlight.text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(escapedText, 'g');
      const colorClass = colorClasses[highlight.color] || colorClasses.yellow;
      content = content.replace(
        regex,
        `<mark class="${colorClass} rounded px-1" data-highlight-id="${highlight.id}">${highlight.text}</mark>`
      );
    });
    
    return (
      <pre 
        className="whitespace-pre-wrap font-sans text-base leading-relaxed"
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  };

  const startQuiz = () => {
    if (currentSubjectData && currentSubjectData.quizzes) {
      setQuizQuestions(currentSubjectData.quizzes);
      setCurrentQuestion(0);
      setScore(0);
      setQuizCompleted(false);
      setSelectedAnswer(null);
      setShowQuiz(true);
    }
  };

  const handleAnswerSelect = (answerIndex) => {
    setSelectedAnswer(answerIndex);
    if (answerIndex === quizQuestions[currentQuestion].correct) {
      setScore(score + 1);
      toast.success('Correct! 🎉');
    } else {
      toast.error('Incorrect. Try again!');
    }
  };

  const handleNextQuestion = () => {
    if (currentQuestion + 1 < quizQuestions.length) {
      setCurrentQuestion(currentQuestion + 1);
      setSelectedAnswer(null);
    } else {
      setQuizCompleted(true);
    }
  };

  const handleAddNote = async () => {
    if (newNote.title && newNote.content) {
      try {
        const response = await axios.post(`${API}/personal-notes`, {
          subject_name: selectedSubject,
          title: newNote.title,
          content: newNote.content
        }, {
          headers: getAuthHeader()
        });
        
        setUserNotes([...userNotes, response.data]);
        setNewNote({ title: '', content: '' });
        setShowAddNote(false);
        toast.success('Note added!');
      } catch (error) {
        console.error('Error adding note:', error);
        toast.error('Failed to add note');
      }
    }
  };

  const handleEditNote = (note) => {
    setEditingNote(note);
    setShowEditNote(true);
  };

  const handleUpdateNote = async () => {
    if (editingNote && editingNote.title && editingNote.content) {
      try {
        const response = await axios.put(`${API}/personal-notes/${editingNote.id}`, {
          title: editingNote.title,
          content: editingNote.content
        }, {
          headers: getAuthHeader()
        });
        
        setUserNotes(userNotes.map(note => note.id === editingNote.id ? response.data : note));
        setEditingNote(null);
        setShowEditNote(false);
        toast.success('Note updated!');
      } catch (error) {
        console.error('Error updating note:', error);
        toast.error('Failed to update note');
      }
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      try {
        await axios.delete(`${API}/personal-notes/${noteId}`, {
          headers: getAuthHeader()
        });
        
        setUserNotes(userNotes.filter(note => note.id !== noteId));
        toast.success('Note deleted!');
      } catch (error) {
        console.error('Error deleting note:', error);
        toast.error('Failed to delete note');
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <BookOpen className="w-12 h-12 animate-pulse text-primary mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Loading revision content...</p>
        </div>
      </div>
    );
  }

  if (!selectedSubject) {
    return (
      <div className="min-h-screen p-6 md:p-8 bg-gradient-to-br from-primary/5 via-background to-secondary/10">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-primary/10 rounded-2xl mb-4">
              <BookOpen className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4">Revision Hub</h1>
            <p className="text-base md:text-lg text-muted-foreground">
              Select a subject to begin your revision journey
            </p>
          </div>

          <div className="space-y-8">
            {Object.entries(categories).map(([category, subjects]) => (
              <Card key={category} className="border-2 hover:border-primary/30 transition-all">
                <CardHeader>
                  <CardTitle className="text-2xl">{category}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {subjects.map((subject) => {
                      const hasContent = revisionContent.some(item => item.subject_name === subject);
                      return (
                        <Button
                          key={subject}
                          variant="outline"
                          className={`justify-between h-auto py-4 px-5 text-left hover:bg-primary/5 hover:border-primary transition-all ${
                            !hasContent ? 'opacity-50' : ''
                          }`}
                          onClick={() => handleSubjectSelect(category, subject)}
                          disabled={!hasContent}
                        >
                          <span className="font-medium">{subject}</span>
                          <ChevronRight className="w-5 h-5" />
                        </Button>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-secondary/5 to-primary/5">
      <div className="max-w-7xl mx-auto p-6 md:p-8">
        <Button
          variant="ghost"
          onClick={() => {
            setSelectedSubject('');
            setCurrentTopic(null);
            setCurrentSubjectData(null);
          }}
          className="mb-6 hover:bg-secondary"
        >
          ← Back to Subjects
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1">
            <Card className="border-2 lg:sticky lg:top-6 lg:max-h-[calc(100vh-8rem)] lg:overflow-y-auto">
              <CardHeader className="bg-primary/5 sticky top-0 z-10">
                <CardTitle className="text-xl flex items-center gap-2">
                  <BookOpen className="w-5 h-5" />
                  {selectedSubject}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4">
                <div className="space-y-2 mb-6">
                  <h3 className="font-semibold text-sm text-muted-foreground uppercase mb-3">Contents</h3>
                  {currentSubjectData?.topics?.map((topic, index) => (
                    <button
                      key={index}
                      onClick={() => setCurrentTopic(topic)}
                      className={`w-full text-left px-4 py-3 rounded-xl transition-all text-sm ${
                        currentTopic?.title === topic.title
                          ? 'bg-primary text-primary-foreground font-medium'
                          : 'hover:bg-secondary'
                      }`}
                    >
                      {topic.title}
                    </button>
                  ))}
                </div>

                <div className="space-y-3 pt-4 border-t">
                  <Button onClick={startQuiz} className="w-full rounded-full" size="lg">
                    <Brain className="w-4 h-4 mr-2" />
                    Take Quiz
                  </Button>
                  <Button
                    onClick={() => setShowAddNote(true)}
                    variant="outline"
                    className="w-full rounded-full"
                    size="lg"
                  >
                    <StickyNote className="w-4 h-4 mr-2" />
                    Add Personal Note
                  </Button>
                </div>

                {userNotes.length > 0 && (
                  <div className="mt-6 pt-4 border-t">
                    <h3 className="font-semibold text-sm mb-3">Your Notes & Highlights</h3>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {userNotes.map(note => (
                        <div key={note.id} className="p-3 bg-accent/50 rounded-lg">
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1 min-w-0">
                              <p className="font-medium text-sm">{note.title}</p>
                              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                {note.content}
                              </p>
                            </div>
                            <div className="flex gap-1 flex-shrink-0">
                              <button
                                onClick={() => handleEditNote(note)}
                                className="p-1 hover:bg-primary/10 rounded transition-colors"
                                title="Edit note"
                              >
                                <Pencil className="w-3.5 h-3.5 text-primary" />
                              </button>
                              <button
                                onClick={() => handleDeleteNote(note.id)}
                                className="p-1 hover:bg-destructive/10 rounded transition-colors"
                                title="Delete note"
                              >
                                <Trash2 className="w-3.5 h-3.5 text-destructive" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <Card className="lg:col-span-3 border-2 relative" ref={topicContentRef}>
            <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5">
              <CardTitle className="text-2xl">{currentTopic?.title}</CardTitle>
            </CardHeader>
            <CardContent className="p-6 md:p-8">
              <div
                onMouseUp={handleTextSelect}
                className="prose prose-lg max-w-none"
                style={{ cursor: 'text', userSelect: 'text', WebkitUserSelect: 'text', MozUserSelect: 'text' }}
              >
                {renderHighlightedContent()}
              </div>

              {/* Previous/Next Topic Navigation */}
              {currentSubjectData && currentSubjectData.topics && currentSubjectData.topics.length > 1 && (
                <div className="flex items-center justify-between gap-4 mt-8 pt-6 border-t">
                  <Button
                    onClick={() => {
                      const currentIndex = currentSubjectData.topics.findIndex(t => t.title === currentTopic.title);
                      if (currentIndex > 0) {
                        setShouldScrollToTopic(true);
                        setCurrentTopic(currentSubjectData.topics[currentIndex - 1]);
                        setCurrentTopicIndex(currentIndex - 1);
                      }
                    }}
                    disabled={currentSubjectData.topics.findIndex(t => t.title === currentTopic.title) === 0}
                    variant="outline"
                    className="flex items-center gap-2 rounded-full"
                  >
                    <ChevronRight className="w-4 h-4 rotate-180" />
                    Previous Topic
                  </Button>
                  
                  <span className="text-sm text-muted-foreground">
                    Topic {currentSubjectData.topics.findIndex(t => t.title === currentTopic.title) + 1} of {currentSubjectData.topics.length}
                  </span>
                  
                  <Button
                    onClick={() => {
                      const currentIndex = currentSubjectData.topics.findIndex(t => t.title === currentTopic.title);
                      if (currentIndex < currentSubjectData.topics.length - 1) {
                        setShouldScrollToTopic(true);
                        setCurrentTopic(currentSubjectData.topics[currentIndex + 1]);
                        setCurrentTopicIndex(currentIndex + 1);
                      }
                    }}
                    disabled={currentSubjectData.topics.findIndex(t => t.title === currentTopic.title) === currentSubjectData.topics.length - 1}
                    variant="outline"
                    className="flex items-center gap-2 rounded-full"
                  >
                    Next Topic
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              )}

              {/* Highlight Color Menu */}
              {showHighlightMenu && (
                <div
                  className="fixed z-50 bg-white dark:bg-gray-800 shadow-lg rounded-lg p-2 border-2 border-primary/20"
                  style={{
                    left: `${highlightMenuPosition.x}px`,
                    top: `${highlightMenuPosition.y}px`,
                    transform: 'translateX(-50%)'
                  }}
                >
                  <div className="flex items-center gap-1">
                    <p className="text-xs font-medium text-muted-foreground mr-2">Highlight:</p>
                    <button
                      onClick={() => createHighlight('yellow')}
                      className="w-8 h-8 rounded bg-yellow-300 hover:bg-yellow-400 border-2 border-gray-300 transition-colors"
                      title="Yellow"
                    />
                    <button
                      onClick={() => createHighlight('green')}
                      className="w-8 h-8 rounded bg-green-300 hover:bg-green-400 border-2 border-gray-300 transition-colors"
                      title="Green"
                    />
                    <button
                      onClick={() => createHighlight('blue')}
                      className="w-8 h-8 rounded bg-blue-300 hover:bg-blue-400 border-2 border-gray-300 transition-colors"
                      title="Blue"
                    />
                    <button
                      onClick={() => createHighlight('pink')}
                      className="w-8 h-8 rounded bg-pink-300 hover:bg-pink-400 border-2 border-gray-300 transition-colors"
                      title="Pink"
                    />
                    <button
                      onClick={() => createHighlight('purple')}
                      className="w-8 h-8 rounded bg-purple-300 hover:bg-purple-400 border-2 border-gray-300 transition-colors"
                      title="Purple"
                    />
                    <button
                      onClick={() => setShowHighlightMenu(false)}
                      className="ml-2 px-2 py-1 text-xs text-gray-500 hover:text-gray-700"
                    >
                      ✕
                    </button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        <Dialog open={showQuiz} onOpenChange={setShowQuiz}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Quiz: {selectedSubject}</DialogTitle>
            </DialogHeader>
            {!quizCompleted ? (
              <div className="space-y-6">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">
                    Question {currentQuestion + 1} of {quizQuestions.length}
                  </span>
                  <span className="text-sm font-medium">Score: {score}/{quizQuestions.length}</span>
                </div>
                
                <h3 className="text-lg font-semibold">{quizQuestions[currentQuestion]?.question}</h3>
                
                <div className="space-y-2">
                  {quizQuestions[currentQuestion]?.options.map((option, index) => (
                    <button
                      key={index}
                      onClick={() => handleAnswerSelect(index)}
                      disabled={selectedAnswer !== null}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        selectedAnswer === null
                          ? 'border-border hover:border-primary'
                          : selectedAnswer === index
                          ? index === quizQuestions[currentQuestion].correct
                            ? 'border-green-500 bg-green-50'
                            : 'border-red-500 bg-red-50'
                          : index === quizQuestions[currentQuestion].correct
                          ? 'border-green-500 bg-green-50'
                          : 'border-border'
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                
                {selectedAnswer !== null && (
                  <div className="bg-accent/50 p-4 rounded-xl">
                    <p className="text-sm font-medium mb-2">Explanation:</p>
                    <p className="text-sm">{quizQuestions[currentQuestion].explanation}</p>
                  </div>
                )}
                
                {selectedAnswer !== null && (
                  <Button onClick={handleNextQuestion} className="w-full rounded-full">
                    {currentQuestion + 1 === quizQuestions.length ? 'See Results' : 'Next Question'}
                  </Button>
                )}
              </div>
            ) : (
              <div className="text-center py-8 space-y-6">
                <h3 className="text-3xl font-bold">Quiz Complete!</h3>
                <p className="text-5xl font-bold text-primary">{score}/{quizQuestions.length}</p>
                <p className="text-lg">
                  {score === quizQuestions.length ? 'Perfect! 🎉' : score >= quizQuestions.length / 2 ? 'Good job! 👍' : 'Keep studying! 💪'}
                </p>
                <div className="flex gap-3">
                  <Button onClick={() => setShowQuiz(false)} variant="outline" className="flex-1 rounded-full">
                    Close
                  </Button>
                  <Button onClick={startQuiz} className="flex-1 rounded-full">
                    Try Again
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        <Dialog open={showAddNote} onOpenChange={setShowAddNote}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Personal Note</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Note Title"
                value={newNote.title}
                onChange={(e) => setNewNote({ ...newNote, title: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Textarea
                placeholder="Your notes here..."
                value={newNote.content}
                onChange={(e) => setNewNote({ ...newNote, content: e.target.value })}
                rows={6}
                className="w-full"
              />
              <Button onClick={handleAddNote} className="w-full rounded-full">
                Save Note
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={showEditNote} onOpenChange={setShowEditNote}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Personal Note</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <input
                type="text"
                placeholder="Note Title"
                value={editingNote?.title || ''}
                onChange={(e) => setEditingNote({ ...editingNote, title: e.target.value })}
                className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              />
              <Textarea
                placeholder="Your notes here..."
                value={editingNote?.content || ''}
                onChange={(e) => setEditingNote({ ...editingNote, content: e.target.value })}
                rows={6}
                className="w-full"
              />
              <Button onClick={handleUpdateNote} className="w-full rounded-full">
                Update Note
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        {/* Highlight Note Dialog */}
        <Dialog open={showHighlightNote} onOpenChange={setShowHighlightNote}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Add Your Notes to Highlight</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              {currentHighlight && (
                <div className="p-4 bg-accent/30 rounded-lg border-2 border-primary/20">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-4 h-4 rounded ${
                      currentHighlight.color === 'yellow' ? 'bg-yellow-400' :
                      currentHighlight.color === 'green' ? 'bg-green-400' :
                      currentHighlight.color === 'blue' ? 'bg-blue-400' :
                      currentHighlight.color === 'pink' ? 'bg-pink-400' :
                      'bg-purple-400'
                    }`} />
                    <p className="text-sm font-semibold">Highlighted Text:</p>
                  </div>
                  <p className="text-sm text-muted-foreground italic bg-white dark:bg-gray-800 p-3 rounded border">
                    "{currentHighlight.text}"
                  </p>
                </div>
              )}
              <div>
                <label className="text-sm font-medium mb-2 block">
                  Your Personal Notes:
                </label>
                <Textarea
                  placeholder="Add your thoughts, explanations, examples, or anything that helps you understand this concept better..."
                  value={highlightNote}
                  onChange={(e) => setHighlightNote(e.target.value)}
                  rows={6}
                  className="resize-none"
                />
                <p className="text-xs text-muted-foreground mt-2">
                  💡 Tip: This will be saved in your Personal Notes with the highlighted text above it
                </p>
              </div>
              <div className="flex gap-2 justify-end">
                <Button 
                  variant="outline" 
                  onClick={() => {
                    setShowHighlightNote(false);
                    setCurrentHighlight(null);
                    setHighlightNote('');
                  }}
                >
                  Skip
                </Button>
                <Button onClick={saveHighlightNote}>
                  Save Highlight & Notes
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default Revision;
